
import type { RouteObject } from 'react-router-dom';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { lazy } from 'react';
import { hasPermission } from '../utils/rolePermissions';

// Lazy load components
const Login = lazy(() => import('../pages/auth/Login'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
const Projects = lazy(() => import('../pages/projects/Projects'));
const ProjectDetail = lazy(() => import('../pages/projects/ProjectDetail'));
const ProjAssign = lazy(() => import('../pages/projects/ProjAssign'));
const Tasks = lazy(() => import('../pages/tasks/Tasks'));
const ManageTasks = lazy(() => import('../pages/tasks/ManageTasks'));
const DailyTaskEntry = lazy(() => import('../pages/tasks/DailyTaskEntry'));
const Proposals = lazy(() => import('../pages/proposals/Proposals'));
const Files = lazy(() => import('../pages/files/Files'));
const Visits = lazy(() => import('../pages/visits/Visits'));
const Payments = lazy(() => import('../pages/payments/Payments'));
const Expenses = lazy(() => import('../pages/expenses/Expenses'));
const Clients = lazy(() => import('../pages/clients/Clients'));
const Employees = lazy(() => import('../pages/employees/Employees'));
const Attendance = lazy(() => import('../pages/attendance/Attendance'));
const Payroll = lazy(() => import('../pages/payroll/Payroll'));
const ActivityLog = lazy(() => import('../pages/activity-log/ActivityLog'));
const Reports = lazy(() => import('../pages/reports/Reports'));
const Settings = lazy(() => import('../pages/settings/Settings'));
const UserManual = lazy(() => import('../pages/help/UserManual'));
const NotFound = lazy(() => import('../pages/NotFound'));

// Simple auth helpers
const isAuthed = () => !!(localStorage.getItem('access') && localStorage.getItem('currentUser'));

const getStoredUserRole = (): string => {
  try {
    const stored = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
    if (!stored) return '';
    const u = JSON.parse(stored);
    return String(u?.role || '');
  } catch {
    return '';
  }
};

function RequireAuth({ children }: { children: ReactNode }) {
  return isAuthed() ? <>{children}</> : <Navigate to="/login" replace />;
}

function RequireMenuPermission({ children }: { children: ReactNode }) {
  const location = useLocation();
  const role = getStoredUserRole();
  // If we can't infer a role, fall back to auth-only behavior.
  if (!role) return <>{children}</>;

  const menuPath = `${location.pathname}${location.search || ''}`;
  return hasPermission(role, menuPath) ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

function RedirectIfAuth({ children }: { children: ReactNode }) {
  return isAuthed() ? <Navigate to="/dashboard" replace /> : <>{children}</>;
}

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RedirectIfAuth>
        <Login />
      </RedirectIfAuth>
    )
  },
  {
    path: '/login',
    element: (
      <RedirectIfAuth>
        <Login />
      </RedirectIfAuth>
    )
  },
  {
    path: '/dashboard',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Dashboard />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/projects',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Projects />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/projects/:id',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <ProjectDetail />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/proj-assign',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <ProjAssign />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/tasks',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Tasks />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/tasks/manage',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <ManageTasks />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/tasks/daily-entry',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <DailyTaskEntry />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/proposals',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Proposals />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/files',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Files />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/visits',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Visits />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/payments',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Payments />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/expenses',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Expenses />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/clients',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Clients />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/employees',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Employees />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/attendance',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Attendance />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/payroll',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Payroll />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/activity-log',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <ActivityLog />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/reports',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Reports />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/settings',
    element: (
      <RequireAuth>
        <RequireMenuPermission>
          <Settings />
        </RequireMenuPermission>
      </RequireAuth>
    )
  },
  {
    path: '/help',
    element: (
      <RequireAuth>
        <UserManual />
      </RequireAuth>
    )
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />
  },
  {
    path: '*',
    element: <NotFound />
  }
];

export default routes;
